import {
  buildModalPayload,
  parseModalPayload,
  registerModal,
  type ModalParams
} from '@gat-ship/vue-components'
import { StakeholderDetailModalProvider } from '@gat-ship/stakeholder-details'
import { TenantUserDetailModalProvider } from '@gat-ship/tenant-users'
import { AgencyDetailModalProvider } from '@gat-ship/agency'
import { AccessRoleDetailRouteProvider } from '@gat-ship/access-control'
import { AdministrationModal } from '@gat-ship/administration'
import { UserSettingsModal } from '@gat-ship/user-settings'
import { CreatePortcallModalProvider } from '@gat-ship/create-portcall'
import { PortDetailModalProvider } from '@gat-ship/ports'

let registered = false

/** Keep only the param keys with a non-empty value. */
function compact(params: Record<string, string | undefined>): ModalParams {
  const result: ModalParams = {}
  for (const [key, value] of Object.entries(params)) {
    if (value != null && value !== '') result[key] = value
  }
  return result
}

/**
 * Register the app's persistent modal kinds with the central host
 * (central-modal-host plan, 1.5 + 2.1). Each kind owns its `?modals=` payload
 * shape via `toQuery`/`fromQuery` (the canonical grammar from decision 0.3:
 * primary key first, then optional short-keyed fields — `s=` section, `t=`
 * type). Kind tokens are kebab-case and match the codec tokens.
 */
export function registerAppModals(): void {
  if (registered) return
  registered = true

  registerModal('stakeholder', {
    component: StakeholderDetailModalProvider,
    persist: true,
    label: 'Stakeholder',
    toQuery: (p) =>
      buildModalPayload(p.id ?? '', {
        s: p.stakeholderSection,
        t: p.stakeholderType
      }),
    fromQuery: (payload) => {
      const { primary, fields } = parseModalPayload(payload)
      return compact({
        id: primary,
        stakeholderSection: fields.s,
        stakeholderType: fields.t
      })
    }
  })

  registerModal('tenant-user', {
    component: TenantUserDetailModalProvider,
    persist: true,
    label: 'Tenant user',
    toQuery: (p) => buildModalPayload(p.id ?? '', { s: p.tenantUserSection }),
    fromQuery: (payload) => {
      const { primary, fields } = parseModalPayload(payload)
      return compact({ id: primary, tenantUserSection: fields.s })
    }
  })

  registerModal('agency', {
    component: AgencyDetailModalProvider,
    persist: true,
    label: 'Agency',
    toQuery: (p) => buildModalPayload(p.id ?? '', { s: p.agencySection }),
    fromQuery: (payload) => {
      const { primary, fields } = parseModalPayload(payload)
      return compact({ id: primary, agencySection: fields.s })
    }
  })

  registerModal('port', {
    component: PortDetailModalProvider,
    persist: true,
    label: 'Port',
    toQuery: (p) =>
      buildModalPayload(p.locationCode ?? '', { s: p.portSection }),
    fromQuery: (payload) => {
      const { primary, fields } = parseModalPayload(payload)
      return compact({ locationCode: primary, portSection: fields.s })
    }
  })

  registerModal('access-role', {
    component: AccessRoleDetailRouteProvider,
    persist: true,
    label: 'Access role',
    toQuery: (p) => p.id ?? '',
    fromQuery: (payload) => compact({ id: parseModalPayload(payload).primary })
  })

  registerModal('admin', {
    component: AdministrationModal,
    persist: true,
    label: 'Administration',
    toQuery: (p) => p.panel ?? '',
    fromQuery: (payload) =>
      compact({ panel: parseModalPayload(payload).primary }),
    // Panel sub-scopes that must be dropped together with the admin modal.
    cleanupQueryKeys: ['nodeId']
  })

  // Transient create flow — no entity id, so it carries no payload; the URL
  // token is just `create-portcall`. Reopens an empty wizard on refresh.
  registerModal('create-portcall', {
    component: CreatePortcallModalProvider,
    persist: true,
    label: 'Create Port Call',
    toQuery: () => '',
    fromQuery: () => ({})
  })

  registerModal('user-settings', {
    component: UserSettingsModal,
    persist: true,
    label: 'Settings',
    toQuery: (p) => p.tab ?? '',
    fromQuery: (payload) => compact({ tab: parseModalPayload(payload).primary })
  })
}
